// ./src/components/features/LostFound/LostReportsList/LostReportsList.tsx
//
// ROADMAP #68 — an event's lost reports: a table on desktop, cards below. Each row: the report's number, what was lost
// (their words), who lost it (name and how to reach them — the crew hold lostfound.manage), where it stands (looking, maybe
// found — naming the item —, returned, closed and why) and when it was lost.
//
// Do-something actions follow the state — match to an item, "not theirs", close, reopen — on the other side of a phone's
// swipe from Edit / Delete. A returned report has none: its item's handover is undone on the item.
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, ActionIcon, Tooltip, Badge, Skeleton, Table } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { LostReport } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  TableDateTime,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";
import { useEntityCommentsDrawer } from "@/components/features/Comments";

import {
  lostFoundCategoryIcon,
  lostFoundCategoryLabel,
  lostFoundNumber,
  lostReportClosedReasonLabel,
  lostReportStateColor,
  lostReportStateLabel,
} from "../lostFoundLabels";

export type LostReportAction = "match" | "unmatch" | "close" | "reopen";

export interface LostReportsListProps {
  data: LostReport[];
  loading: boolean;
  /** ⚠ REQUIRED — whether the FIRST read has come back. ▶ check-list-states */
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  hasQueryNarrowing: boolean;
  onEdit?: (report: LostReport) => void;
  onDelete?: (report: LostReport) => void;
  onAction?: (report: LostReport, action: LostReportAction) => void;
  onAdd?: () => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  isRemoving: (id?: string) => boolean;
  highlightedId?: string | null;
}

interface ActionDef {
  action: LostReportAction;
  label: string;
  icon: string;
  color: string;
  busyKey?: string;
}

const actionsFor = (t: TFunction, report: LostReport): ActionDef[] => {
  switch (report.reportState) {
    case "open":
      return [
        { action: "match", label: t("lostFound:actions.match", { defaultValue: "Match to a found item" }), icon: "Link", color: "blue" },
        { action: "close", label: t("lostFound:actions.close", { defaultValue: "Close" }), icon: "CircleX", color: "gray" },
      ];
    case "matched":
      return [
        { action: "unmatch", label: t("lostFound:actions.unmatch", { defaultValue: "Not theirs — keep looking" }), icon: "Unlink", color: "orange", busyKey: "move:open" },
        { action: "close", label: t("lostFound:actions.close", { defaultValue: "Close" }), icon: "CircleX", color: "gray" },
      ];
    case "closed":
      return [{ action: "reopen", label: t("lostFound:actions.reopen", { defaultValue: "Reopen" }), icon: "ArrowBackUp", color: "gray", busyKey: "move:open" }];
    default:
      return [];
  }
};

const WhatCell: React.FC<{ report: LostReport }> = ({ report }) => {
  const { t } = useTranslation(["lostFound"]);

  return (
    <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
      <Box mt={4} style={{ flexShrink: 0 }}>
        <AppIcon iconName={lostFoundCategoryIcon(report.category)} size={18} />
      </Box>
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Text size="md" fw={600} truncate>
          {lostFoundCategoryLabel(t, report.category)}
        </Text>
        {report.description ? (
          <Text size="md" c="dimmed" lineClamp={2}>
            {report.description}
          </Text>
        ) : null}
      </Stack>
    </Group>
  );
};

const ReporterCell: React.FC<{ report: LostReport }> = ({ report }) => {
  const { t } = useTranslation(["lostFound"]);
  const r = report.reporter;
  const reach = r?.email || r?.phone;

  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Group gap={6} wrap="nowrap">
        <AppIcon iconName={r?.kind === "guest" ? "User" : "AddressBook"} size={16} />
        <Text size="md" fw={500} truncate td={r?.removed ? "line-through" : undefined}>
          {r?.name || t("lostFound:list.unnamed", { defaultValue: "No name given" })}
        </Text>
      </Group>
      {reach ? (
        <Text size="md" c="dimmed" truncate>
          {reach}
        </Text>
      ) : null}
    </Stack>
  );
};

const ReportStateCell: React.FC<{ report: LostReport }> = ({ report }) => {
  const { t } = useTranslation(["lostFound"]);
  const detail =
    report.reportState === "closed"
      ? lostReportClosedReasonLabel(t, report.closedReason)
      : report.matchedItem
        ? lostFoundNumber("Item", report.matchedItem.sequenceNo)
        : null;

  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Badge size="md" variant="light" color={lostReportStateColor(report.reportState)} style={{ alignSelf: "flex-start" }}>
        {lostReportStateLabel(t, report.reportState)}
      </Badge>
      {/* Made in test mode — hidden once the event is live (like a test guest). Its own line: beside the state it cut the
          state's words short (owner, 2026-10-23). */}
      {report.isTest ? (
        <Badge size="sm" variant="outline" color="orange" style={{ alignSelf: "flex-start" }}>
          {t("lostFound:list.test", { defaultValue: "Test" })}
        </Badge>
      ) : null}
      {detail ? (
        <Text size="md" c="dimmed" truncate>
          {detail}
        </Text>
      ) : null}
    </Stack>
  );
};

interface RowProps {
  report: LostReport;
  onEdit?: (report: LostReport) => void;
  onDelete?: (report: LostReport) => void;
  /** The crew's notes on it — a crew-only comment thread. */
  onComments?: (report: LostReport) => void;
  onAction?: (report: LostReport, action: LostReportAction) => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  highlighted: boolean;
  isDeleting: boolean;
}

const RowActions: React.FC<RowProps> = ({ report, onEdit, onDelete, onComments, onAction, isUpdating, isDeleting }) => {
  const { t } = useTranslation(["lostFound", "common"]);
  const canEdit = recordHasRight(report, "edit");
  const rowBusy = isUpdating(report.id);
  const moves = onAction && canEdit ? actionsFor(t, report) : [];

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      {moves.map((m) => (
        <Tooltip key={m.action} label={m.label} withArrow>
          <ActionIcon
            variant="subtle"
            color={m.color}
            size="lg"
            loading={m.busyKey ? isUpdating(report.id, m.busyKey) : false}
            disabled={isDeleting || (rowBusy && !(m.busyKey && isUpdating(report.id, m.busyKey)))}
            onClick={() => onAction?.(report, m.action)}
            aria-label={m.label}
          >
            <AppIcon iconName={m.icon} size={18} />
          </ActionIcon>
        </Tooltip>
      ))}
      {onComments ? (
        <Tooltip label={t("lostFound:actions.notes", { defaultValue: "Crew notes" })} withArrow>
          <ActionIcon variant="subtle" color="neutral" size="lg" disabled={isDeleting} onClick={() => onComments(report)} aria-label={t("lostFound:actions.notes", { defaultValue: "Crew notes" })}>
            <AppIcon iconName="MessageCircle" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onEdit && canEdit ? (
        <Tooltip label={t("common:buttons.edit", { defaultValue: "Edit" })} withArrow>
          <ActionIcon variant="subtle" color="neutral" size="lg" disabled={isDeleting || rowBusy} onClick={() => onEdit(report)} aria-label={t("common:buttons.edit", { defaultValue: "Edit" })}>
            <AppIcon iconName="Pencil" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onDelete && recordHasRight(report, "remove") ? (
        <Tooltip label={t("common:buttons.delete", { defaultValue: "Delete" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="error"
            size="lg"
            loading={isDeleting}
            disabled={rowBusy}
            onClick={() => onDelete(report)}
            aria-label={t("common:buttons.delete", { defaultValue: "Delete" })}
          >
            <AppIcon iconName="Trash" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
};

const ReportTableRow: React.FC<RowProps> = (p) => (
  <Table.Tr style={{ opacity: p.isDeleting ? 0.5 : 1, backgroundColor: p.highlighted ? HIGHLIGHT_ROW_BG : undefined, transition: "background-color 150ms ease, opacity 150ms ease" }}>
    {/* The number alone — the column says "No." (owner, 2026-10-23). */}
    <Table.Td style={{ width: 56 }}>
      <Text size="md" c="dimmed">
        {p.report.sequenceNo ?? ""}
      </Text>
    </Table.Td>
    <Table.Td>
      <WhatCell report={p.report} />
    </Table.Td>
    <Table.Td style={{ width: 220 }}>
      <ReporterCell report={p.report} />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <ReportStateCell report={p.report} />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <TableDateTime value={p.report.lostAt ?? p.report.createdAt ?? null} />
    </Table.Td>
    <Table.Td style={{ width: 190 }}>
      <RowActions {...p} />
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const ReportCardComponent: React.FC<CardProps> = (p) => {
  const { t } = useTranslation(["lostFound", "common"]);
  const { report } = p;

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    const canEdit = recordHasRight(report, "edit");

    if (p.onAction && canEdit) {
      for (const m of actionsFor(t, report)) {
        actions.push({
          key: m.action,
          icon: m.icon,
          label: m.label,
          color: m.color,
          onClick: () => p.onAction?.(report, m.action),
          loading: m.busyKey ? p.isUpdating(report.id, m.busyKey) : false,
          disabled: p.isDeleting,
          secondary: true,
        });
      }
    }

    if (p.onComments) {
      actions.push({ key: "notes", icon: "MessageCircle", label: t("lostFound:actions.notesShort", { defaultValue: "Notes" }), color: "gray", onClick: () => p.onComments?.(report), disabled: p.isDeleting, secondary: true });
    }

    if (p.onEdit && canEdit) {
      actions.push({ key: "edit", icon: "Edit", label: t("common:buttons.edit", { defaultValue: "Edit" }), color: "blue", onClick: () => p.onEdit?.(report), disabled: p.isDeleting });
    }

    if (p.onDelete && recordHasRight(report, "remove")) {
      actions.push({ key: "delete", icon: "Trash", label: t("common:buttons.delete", { defaultValue: "Delete" }), color: "red", onClick: () => p.onDelete?.(report), loading: p.isDeleting, disabled: p.isDeleting });
    }

    return actions;
  }, [p, report, t]);

  const cardBody = (showActions: boolean) => (
    <Paper p="md" withBorder radius="md" opacity={p.isDeleting ? 0.5 : 1} style={{ transition: "opacity 150ms ease", ...(p.highlighted ? HIGHLIGHT_STYLE : {}) }}>
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" justify="space-between" align="flex-start">
            <WhatCell report={report} />
            <Text size="md" c="dimmed" style={{ flexShrink: 0 }}>
              {lostFoundNumber("Report", report.sequenceNo)}
            </Text>
          </Group>
          <Box mt={8}>
            <ReporterCell report={report} />
          </Box>
          <Box mt={6}>
            <ReportStateCell report={report} />
          </Box>
        </Box>
        {showActions && <RowActions {...p} />}
      </Group>
    </Paper>
  );

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={p.forceClose}
            onForceCloseComplete={p.onForceCloseComplete}
            onSwipeStateChange={p.onSwipeStateChange}
            disabled={p.isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false)}
          </SwipeableCard>
        ) : (
          cardBody(false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true)}</Box>
    </>
  );
};

const ReportCard = React.memo(ReportCardComponent);

const LostReportsListComponent: React.FC<LostReportsListProps> = ({
  data,
  loading,
  isLoaded,
  loadingMore,
  hasMore,
  onLoadMore,
  hasQueryNarrowing,
  onEdit,
  onDelete,
  onAction,
  onAdd,
  isUpdating,
  isRemoving,
  highlightedId,
}) => {
  const { t } = useTranslation(["lostFound", "common"]);
  const { isDesktop } = useAppEnvironment();
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  // The crew's notes on a row — a crew-only comment thread (the public side refuses this kind). Mounted once, below.
  const comments = useEntityCommentsDrawer();
  const openComments = useCallback(
    (report: LostReport) => {
      closeAllCards();
      comments.open({ eventId: report.eventId ?? "", entityKind: "lost_report", entityId: report.id ?? "", title: lostFoundNumber("Report", report.sequenceNo) });
    },
    [comments, closeAllCards]
  );
  // The tablet card's row buttons, one per handler (▶ check-card-skeletons).
  const cardActions = countCardActions(onEdit, onDelete, openComments, onAction);

  const handleEdit = useCallback(
    (report: LostReport) => {
      closeAllCards();
      onEdit?.(report);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (report: LostReport) => {
      closeAllCards();
      onDelete?.(report);
    },
    [onDelete, closeAllCards]
  );

  const handleAction = useCallback(
    (report: LostReport, action: LostReportAction) => {
      closeAllCards();
      onAction?.(report, action);
    },
    [onAction, closeAllCards]
  );

  const header = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th style={{ width: 56 }}>{t("lostFound:list.columns.number", { defaultValue: "No." })}</Table.Th>
        <Table.Th>{t("lostFound:list.columns.lost", { defaultValue: "What was lost" })}</Table.Th>
        <Table.Th style={{ width: 220 }}>{t("lostFound:list.columns.reporter", { defaultValue: "Who" })}</Table.Th>
        <Table.Th style={{ width: 180 }}>{t("lostFound:list.columns.reportState", { defaultValue: "Status" })}</Table.Th>
        <Table.Th style={{ width: 150 }}>{t("lostFound:list.columns.lostAt", { defaultValue: "Lost" })}</Table.Th>
        <Table.Th style={{ width: 190 }} />
      </Table.Tr>
    </Table.Thead>
  );

  // ⚠ The FIRST read only. ▶ npm run check-list-states
  if (!isLoaded) {
    return (
      <Stack gap="xs" aria-busy="true">
        <Box visibleFrom="md">
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {header}
              <Table.Tbody>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Skeleton height={16} width={60} />
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={6}>
                        <Skeleton height={16} width={140} />
                        <Skeleton height={14} width="70%" />
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={6}>
                        <Skeleton height={16} width={120} />
                        <Skeleton height={14} width={160} />
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={22} width={100} radius="xl" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={90} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap" justify="flex-end">
                        <Skeleton circle height={34} width={34} />
                        <Skeleton circle height={34} width={34} />
                        <Skeleton circle height={34} width={34} />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Box>
        {[0, 1, 2].map((i) => (
          <Paper key={`p-${i}`} withBorder radius="md" p="md" hiddenFrom="sm">
            <CardSkeletonLines count={3} gap={8} />
          </Paper>
        ))}
        {[0, 1, 2].map((i) => (
          <Paper key={`t-${i}`} withBorder radius="md" p="md" visibleFrom="sm" hiddenFrom="md">
            <Group gap="md" wrap="nowrap" justify="space-between">
              <CardSkeletonLines count={3} gap={8} />
              <CardSkeletonActions count={cardActions} />
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("lostFound:list.noReportsMatch", { defaultValue: "No reports match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("lostFound:list.noReportsTitle", { defaultValue: "No lost reports yet" })}
          description={t("lostFound:list.noReportsBody", {
            defaultValue: "Guests tell you what they lost on the event site; you can also take a report at the desk or by phone. Match it to a found item when one turns up.",
          })}
          iconName="Search"
          actionLabel={onAdd ? t("lostFound:actions.takeReport", { defaultValue: "Take a report" }) : undefined}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (report: LostReport): RowProps => ({
    report,
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    onComments: openComments,
    onAction: onAction ? handleAction : undefined,
    isUpdating,
    highlighted: report.id === highlightedId,
    isDeleting: isRemoving(report.id),
  });

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            {header}
            <Table.Tbody>
              {data.map((report) => (
                <ReportTableRow key={report.id} {...rowProps(report)} />
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {loadMore}
        {comments.drawer}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((report) => (
          <ReportCard
            key={report.id}
            {...rowProps(report)}
            forceClose={shouldForceClose(report.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(report.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(report.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
      {comments.drawer}
    </Stack>
  );
};

export const LostReportsList = React.memo(LostReportsListComponent);
